// PlayCanvas battle renderer (engine-only, code-first, WebGL2). Replaces the Canvas-2D
// world renderer with a lit 3D scene: the hand-authored map as ground + real cover
// geometry, procedurally-instanced low-poly squads posed by posture (see figures.ts),
// pooled vehicles/heroes/structures/points, capture-point beams, and battle effects.
// The sim stays entirely outside the engine loop — every frame `draw()` reads sim state,
// interpolates, and drives the scene; PlayCanvas renders it (GDD §8.2). The screen-space
// HUD + world overlay are drawn separately on the 2D canvas that sits on top.

import * as pc from "playcanvas";
import { angleDelta, type Vec2 } from "../engine/vec";
import type { CoverDef, Faction, Squad, Structure } from "../sim/types";
import type { World } from "../sim/world";
import type { IWorldRenderer, ViewState } from "../render/renderer";
import { PcCamera } from "./pc-camera";
import { SoldierBatches } from "./figures";

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

const hexColor = (h: string): pc.Color => {
  const n = parseInt(h.slice(1), 16);
  return new pc.Color(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
};

function parseColor(s: string | undefined, fb: pc.Color): pc.Color {
  if (!s) return fb;
  if (s[0] === "#") return hexColor(s);
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const p = m[1].split(",").map((v) => parseFloat(v));
    return new pc.Color((p[0] || 0) / 255, (p[1] || 0) / 255, (p[2] || 0) / 255);
  }
  return fb;
}

interface MatOpts {
  emissive?: string;
  intensity?: number;
  additive?: boolean;
  gloss?: number;
}
function mat(diffuse: string, o: MatOpts = {}): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.useMetalness = false;
  m.diffuse = hexColor(diffuse);
  m.gloss = o.gloss ?? 0.25;
  m.specular = new pc.Color(0.08, 0.08, 0.08);
  if (o.emissive) {
    m.emissive = hexColor(o.emissive);
    m.emissiveIntensity = o.intensity ?? 1;
  }
  if (o.additive) {
    m.blendType = pc.BLEND_ADDITIVE;
    m.depthWrite = false;
    m.useLighting = false;
    m.diffuse = new pc.Color(0, 0, 0);
  }
  m.update();
  return m;
}

export class PcRenderer implements IWorldRenderer {
  readonly camera: PcCamera;
  private app: pc.Application;
  private canvas: HTMLCanvasElement;
  private camEnt: pc.Entity;
  private worldLayer: pc.Layer;
  private soldiers: SoldierBatches;

  private structures = new Map<number, pc.Entity>();
  private vehicles = new Map<number, pc.Entity>();
  private heroes = new Map<number, { root: pc.Entity; aura: pc.Entity }>();
  private points: pc.Entity[] = [];

  // effect glow-disc pool
  private glowPool: pc.Entity[] = [];
  private glowMats: Record<string, pc.StandardMaterial>;
  private planeMesh: pc.Mesh;

  // materials
  private M: Record<string, pc.StandardMaterial> = {};
  private boxMesh: pc.Mesh;
  private cylMesh: pc.Mesh;

  private lastWorld: World | null = null;
  private lastView: ViewState | null = null;

  constructor(mount: HTMLElement) {
    this.canvas = document.createElement("canvas");
    this.canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;z-index:1;pointer-events:none;";
    mount.insertBefore(this.canvas, mount.firstChild);

    const app = new pc.Application(this.canvas, {
      graphicsDeviceOptions: { antialias: true, alpha: false, powerPreference: "high-performance" },
    });
    this.app = app;
    app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
    app.setCanvasResolution(pc.RESOLUTION_AUTO);
    app.graphicsDevice.maxPixelRatio = Math.min(2, window.devicePixelRatio || 1);

    this.worldLayer = app.scene.layers.getLayerById(pc.LAYERID_WORLD)!;
    const dev = app.graphicsDevice;
    this.boxMesh = pc.Mesh.fromGeometry(dev, new pc.BoxGeometry({}));
    this.cylMesh = pc.Mesh.fromGeometry(dev, new pc.CylinderGeometry({ radius: 0.5, height: 1, capSegments: 16 }));
    this.planeMesh = pc.Mesh.fromGeometry(dev, new pc.PlaneGeometry({ halfExtents: new pc.Vec2(0.5, 0.5) }));

    this.buildMaterials();

    // ---- camera ----
    this.camEnt = new pc.Entity("camera");
    this.camEnt.addComponent("camera", {
      projection: pc.PROJECTION_PERSPECTIVE,
      fov: 42,
      nearClip: 5,
      farClip: 7000,
      clearColor: hexColor("#0a0908"),
    });
    app.root.addChild(this.camEnt);
    this.camera = new PcCamera(app, this.camEnt);

    // ---- lighting: NW keylight + soft shadows + warm ambient ----
    app.scene.ambientLight = new pc.Color(0.34, 0.32, 0.3);
    const sun = new pc.Entity("sun");
    sun.addComponent("light", {
      type: "directional",
      color: hexColor("#ffe6c0"),
      intensity: 1.35,
      castShadows: true,
      shadowType: pc.SHADOW_PCF5,
      shadowResolution: 2048,
      shadowDistance: 4200,
      shadowBias: 0.06,
      normalOffsetBias: 0.06,
      numCascades: 1,
    });
    sun.setLocalEulerAngles(52, 132, 0); // pitched down, from the NW
    app.root.addChild(sun);
    const fill = new pc.Entity("fill");
    fill.addComponent("light", { type: "directional", color: hexColor("#8aa0c0"), intensity: 0.35, castShadows: false });
    fill.setLocalEulerAngles(40, -40, 0);
    app.root.addChild(fill);

    this.soldiers = new SoldierBatches(app, this.worldLayer, true);

    // effect glow pool
    this.glowMats = {
      fire: mat("#000", { additive: true, emissive: "#ffb040", intensity: 2.2 }),
      muzzle: mat("#000", { additive: true, emissive: "#ffe89a", intensity: 2.6 }),
      bloodLegion: mat("#000", { additive: true, emissive: "#b01818", intensity: 1.6 }),
      bloodKindled: mat("#000", { additive: true, emissive: "#ff7a1e", intensity: 1.8 }),
      dust: mat("#000", { additive: true, emissive: "#7d7566", intensity: 1.1 }),
      smoke: mat("#000", { additive: true, emissive: "#4a4640", intensity: 0.9 }),
      explosion: mat("#000", { additive: true, emissive: "#ffb060", intensity: 2.4 }),
    };
    for (let i = 0; i < 320; i++) {
      const e = new pc.Entity();
      e.addComponent("render", { meshInstances: [new pc.MeshInstance(this.planeMesh, this.glowMats.fire)] });
      e.enabled = false;
      app.root.addChild(e);
      this.glowPool.push(e);
    }

    app.on("framerender", () => this.drawImmediate());
    app.autoRender = true;
    app.start();
  }

  // ------------------------------------------------------------------ setup
  private buildMaterials(): void {
    this.M.ground = this.makeGroundMaterial();
    this.M.road = mat("#1c1916", { gloss: 0.35 });
    this.M.wall = mat("#5a5348");
    this.M.wallTop = mat("#736a5b");
    this.M.sandbag = mat("#7a6a4c");
    this.M.rubble = mat("#3c372f");
    this.M.rubbleBase = mat("#2c2822");
    this.M.crater = mat("#141110", { gloss: 0.1 });
    this.M.building = mat("#413a31");
    this.M.buildingRoof = mat("#2c2721");
    this.M.buildingGarrison = mat("#4a4238");
    this.M.legionBody = mat("#525b66");
    this.M.legionTrim = mat("#8a1f2b");
    this.M.legionSteel = mat("#828d99");
    this.M.kindledBody = mat("#3f382f");
    this.M.kindledTrim = mat("#c25412");
    this.M.kindledSteel = mat("#6a5d4f");
    this.M.generator = mat("#3c4650", { emissive: "#2a90c0", intensity: 0.3 });
    this.M.generatorGlow = mat("#000", { additive: true, emissive: "#7fd0ff", intensity: 1.6 });
    this.M.dark = mat("#1a1714");
    this.M.legionFlag = mat("#c33530", { emissive: "#c33530", intensity: 0.4 });
    this.M.kindledFlag = mat("#e0771b", { emissive: "#e0771b", intensity: 0.4 });
    this.M.neutralFlag = mat("#8a8577");
    this.M.legionBeam = mat("#000", { additive: true, emissive: "#c33530", intensity: 1.4 });
    this.M.kindledBeam = mat("#000", { additive: true, emissive: "#ff8a2c", intensity: 1.4 });
    this.M.heroAuraLegion = mat("#000", { additive: true, emissive: "#ffd778", intensity: 1.2 });
    this.M.heroAuraKindled = mat("#000", { additive: true, emissive: "#ff7a1e", intensity: 1.3 });
    this.M.heroWeaponLegion = mat("#000", { additive: true, emissive: "#fff2c0", intensity: 2 });
    this.M.heroWeaponKindled = mat("#000", { additive: true, emissive: "#ff9a3c", intensity: 2 });
  }

  private makeGroundMaterial(): pc.StandardMaterial {
    const c = document.createElement("canvas");
    c.width = c.height = 512;
    const g = c.getContext("2d")!;
    g.fillStyle = "#20211d";
    g.fillRect(0, 0, 512, 512);
    // ashen district mottling
    for (let i = 0; i < 900; i++) {
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const r = 4 + Math.random() * 26;
      const t = Math.random();
      g.fillStyle = t < 0.5 ? `rgba(38,44,36,${0.15 + Math.random() * 0.2})` : `rgba(58,52,44,${0.12 + Math.random() * 0.2})`;
      g.beginPath();
      g.arc(x, y, r, 0, Math.PI * 2);
      g.fill();
    }
    // faint cracks
    g.strokeStyle = "rgba(10,9,8,0.5)";
    g.lineWidth = 1.5;
    for (let i = 0; i < 40; i++) {
      g.beginPath();
      let x = Math.random() * 512;
      let y = Math.random() * 512;
      g.moveTo(x, y);
      for (let s = 0; s < 5; s++) {
        x += (Math.random() - 0.5) * 60;
        y += (Math.random() - 0.5) * 60;
        g.lineTo(x, y);
      }
      g.stroke();
    }
    const tex = new pc.Texture(this.app.graphicsDevice, {
      width: 512,
      height: 512,
      format: pc.PIXELFORMAT_RGBA8,
      mipmaps: true,
      addressU: pc.ADDRESS_REPEAT,
      addressV: pc.ADDRESS_REPEAT,
      minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
      magFilter: pc.FILTER_LINEAR,
    });
    tex.setSource(c);
    const m = new pc.StandardMaterial();
    m.useMetalness = false;
    m.diffuseMap = tex;
    m.diffuseMapTiling = new pc.Vec2(10, 7);
    m.gloss = 0.12;
    m.specular = new pc.Color(0.03, 0.03, 0.03);
    m.update();
    return m;
  }

  /** Build the static, hand-authored map geometry once (GDD §7.2 / §4.4). */
  buildMap(world: World): void {
    const mw = world.map.width;
    const mh = world.map.height;
    // ground
    const ground = new pc.Entity("ground");
    ground.addComponent("render", { meshInstances: [new pc.MeshInstance(this.planeMesh, this.M.ground)] });
    ground.setLocalScale(mw + 400, 1, mh + 400);
    ground.setPosition(mw / 2, 0, mh / 2);
    this.app.root.addChild(ground);

    for (const c of world.cover) this.buildCover(c);
    this.buildPoints(world);
  }

  private box(m: pc.StandardMaterial, x: number, y: number, z: number, sx: number, sy: number, sz: number, ry = 0, mesh = this.boxMesh): pc.Entity {
    const e = new pc.Entity();
    e.addComponent("render", { meshInstances: [new pc.MeshInstance(mesh, m)] });
    e.setLocalScale(sx, sy, sz);
    e.setLocalEulerAngles(0, ry, 0);
    e.setPosition(x, y, z);
    this.app.root.addChild(e);
    return e;
  }

  private buildCover(c: CoverDef): void {
    switch (c.kind) {
      case "road": {
        const e = this.box(this.M.road, c.x! + c.w! / 2, 0.4, c.y! + c.h! / 2, c.w!, 0.8, c.h!);
        void e;
        break;
      }
      case "ruin": {
        const r = c.radius!;
        this.box(this.M.rubbleBase, c.x!, 3, c.y!, r * 1.7, 6, r * 1.7, 0, this.cylMesh);
        const n = 8;
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2 + (c.x! % 6);
          const rr = r * 0.8;
          this.box(this.M.rubble, c.x! + Math.cos(a) * rr, 14, c.y! + Math.sin(a) * rr, 30, 28, 16, a * R2D);
        }
        break;
      }
      case "crater": {
        const cx = (c.a!.x + c.b!.x) / 2;
        const cy = (c.a!.y + c.b!.y) / 2;
        const r = Math.max(24, Math.hypot(c.a!.x - c.b!.x, c.a!.y - c.b!.y));
        this.box(this.M.crater, cx, 0.5, cy, r * 2, 1, r * 1.5, 0, this.cylMesh);
        break;
      }
      case "wall":
      case "sandbag": {
        const isWall = c.kind === "wall";
        const dx = c.b!.x - c.a!.x;
        const dy = c.b!.y - c.a!.y;
        const len = Math.hypot(dx, dy);
        const ang = -Math.atan2(dy, dx) * R2D;
        const cx = (c.a!.x + c.b!.x) / 2;
        const cy = (c.a!.y + c.b!.y) / 2;
        if (isWall) {
          this.box(this.M.wall, cx, 15, cy, len, 30, 14, ang);
          this.box(this.M.wallTop, cx, 30, cy, len, 5, 16, ang);
        } else {
          // sandbag berm: lower, wider, bumpy
          this.box(this.M.sandbag, cx, 9, cy, len, 18, 24, ang);
          const steps = Math.max(2, Math.round(len / 26));
          for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            this.box(this.M.sandbag, c.a!.x + dx * t, 18, c.a!.y + dy * t, 16, 12, 16, 0, this.cylMesh);
          }
        }
        break;
      }
      case "building": {
        const h = c.garrison ? 46 : 70;
        this.box(c.garrison ? this.M.buildingGarrison : this.M.building, c.x! + c.w! / 2, h / 2, c.y! + c.h! / 2, c.w!, h, c.h!);
        this.box(this.M.buildingRoof, c.x! + c.w! / 2, h + 2, c.y! + c.h! / 2, c.w! + 6, 4, c.h! + 6);
        break;
      }
    }
  }

  private buildPoints(world: World): void {
    for (const p of world.points) {
      const root = new pc.Entity("point");
      root.setPosition(p.pos.x, 0, p.pos.y);
      this.app.root.addChild(root);
      // base disc
      const disc = new pc.Entity();
      disc.addComponent("render", { meshInstances: [new pc.MeshInstance(this.cylMesh, this.M.dark)] });
      disc.setLocalScale(60, 2, 60);
      disc.setPosition(p.pos.x, 1, p.pos.y);
      this.app.root.addChild(disc);
      // pole
      this.box(this.M.dark, p.pos.x, 22, p.pos.y, 3, 44, 3);
      // flag (owner-colored; updated each frame)
      const flag = new pc.Entity("flag");
      flag.addComponent("render", { meshInstances: [new pc.MeshInstance(this.boxMesh, this.M.neutralFlag)] });
      flag.setLocalScale(24, 14, 2);
      flag.setPosition(p.pos.x + 12, 40, p.pos.y);
      this.app.root.addChild(flag);
      // beam (owner-colored additive column)
      const beam = new pc.Entity("beam");
      beam.addComponent("render", { meshInstances: [new pc.MeshInstance(this.cylMesh, this.M.legionBeam)] });
      beam.setLocalScale(14, 200, 14);
      beam.setPosition(p.pos.x, 100, p.pos.y);
      beam.enabled = false;
      this.app.root.addChild(beam);
      root.tags.add(String(this.points.length));
      // stash children on the root via arbitrary props
      (root as any)._flag = flag;
      (root as any)._beam = beam;
      this.points.push(root);
    }
  }

  // ------------------------------------------------------------------ frame
  draw(world: World, view: ViewState): void {
    if (!this.lastWorld) this.buildMap(world);
    this.lastWorld = world;
    this.lastView = view;
    this.camera.sync();

    this.updateStructures(world);
    this.updatePoints(world);
    this.updateVehiclesHeroes(world, view);
    this.updateSoldiers(world, view);
    this.updateGlows(world);
  }

  private interp(prev: Vec2, cur: Vec2, a: number): Vec2 {
    return { x: prev.x + (cur.x - prev.x) * a, y: prev.y + (cur.y - prev.y) * a };
  }

  private facingInterp(pf: number, f: number, a: number): number {
    return pf + angleDelta(pf, f) * a;
  }

  private updateSoldiers(world: World, view: ViewState): void {
    this.soldiers.begin();
    for (const sq of world.squads) {
      if (sq.dead) continue;
      const role = sq.def.role;
      if (role === "vehicle" || role === "hero") continue;
      const sil = sq.def.silhouette;
      for (const m of sq.models) {
        if (!m.alive) continue;
        const p = this.interp(m.prevPos, m.pos, view.alpha);
        const f = this.facingInterp(m.prevFacing, m.facing, view.alpha);
        this.soldiers.add(sq.faction, sil, p.x, p.y, f, m.posture, view.animTime + m.bob);
      }
    }
    this.soldiers.end();
  }

  private matForFaction(f: Faction, part: "body" | "trim" | "steel"): pc.StandardMaterial {
    if (f === "legion") return part === "body" ? this.M.legionBody : part === "trim" ? this.M.legionTrim : this.M.legionSteel;
    return part === "body" ? this.M.kindledBody : part === "trim" ? this.M.kindledTrim : this.M.kindledSteel;
  }

  private updateStructures(world: World): void {
    const live = new Set<number>();
    for (const s of world.structures) {
      live.add(s.id);
      let e = this.structures.get(s.id);
      if (!e) {
        e = this.buildStructure(s);
        this.structures.set(s.id, e);
      }
      e.setPosition(s.pos.x, 0, s.pos.y);
      e.enabled = s.hp > 0;
    }
    for (const [id, e] of this.structures) {
      if (!live.has(id)) {
        e.destroy();
        this.structures.delete(id);
      }
    }
  }

  private buildStructure(s: Structure): pc.Entity {
    const root = new pc.Entity("struct");
    this.app.root.addChild(root);
    const body = this.matForFaction(s.faction, "body");
    const trim = this.matForFaction(s.faction, "trim");
    const r = s.radius;
    const add = (m: pc.StandardMaterial, x: number, y: number, z: number, sx: number, sy: number, sz: number, mesh = this.boxMesh) => {
      const e = new pc.Entity();
      e.addComponent("render", { meshInstances: [new pc.MeshInstance(mesh, m)] });
      e.setLocalScale(sx, sy, sz);
      e.setLocalPosition(x, y, z);
      root.addChild(e);
    };
    switch (s.kind) {
      case "hq":
        add(body, 0, r * 0.8, 0, r * 2, r * 1.6, r * 2);
        add(trim, 0, r * 1.0, 0, r * 1.2, r * 1.2, r * 1.2);
        add(trim, 0, r * 2.0, 0, 6, r * 1.3, 6); // banner pole trim
        break;
      case "barracks":
        add(body, 0, r * 0.6, 0, r * 2, r * 1.2, r * 1.6);
        add(trim, 0, r * 1.2, 0, r * 2, 6, r * 1.6);
        break;
      case "generator":
        add(this.M.generator, 0, r * 0.7, 0, r * 1.6, r * 1.4, r * 1.6, this.cylMesh);
        add(this.M.generatorGlow, 0, r * 1.5, 0, r * 0.9, 6, r * 0.9, this.cylMesh);
        break;
      case "turret":
        add(this.M.legionSteel, 0, r * 0.5, 0, r * 1.4, r, r * 1.4, this.cylMesh);
        add(this.M.dark, r * 0.9, r * 0.9, 0, r * 1.6, 6, 6);
        break;
      case "outpost":
        add(body, 0, r * 0.6, 0, r * 1.4, r * 1.2, r * 1.4);
        add(trim, 0, r * 1.3, 0, r * 1.5, 4, r * 1.5);
        break;
    }
    return root;
  }

  private updatePoints(world: World): void {
    world.points.forEach((p, i) => {
      const root = this.points[i];
      if (!root) return;
      const flag = (root as any)._flag as pc.Entity;
      const beam = (root as any)._beam as pc.Entity;
      const flagMat = p.owner === "legion" ? this.M.legionFlag : p.owner === "kindled" ? this.M.kindledFlag : this.M.neutralFlag;
      flag.render!.meshInstances[0].material = flagMat;
      const beamOwner = p.owner !== "neutral" ? p.owner : p.capturing !== "neutral" ? p.capturing : null;
      if (beamOwner) {
        beam.enabled = true;
        beam.render!.meshInstances[0].material = beamOwner === "legion" ? this.M.legionBeam : this.M.kindledBeam;
      } else {
        beam.enabled = false;
      }
    });
  }

  private updateVehiclesHeroes(world: World, view: ViewState): void {
    const liveV = new Set<number>();
    const liveH = new Set<number>();
    for (const sq of world.squads) {
      if (sq.dead) continue;
      if (sq.def.role === "vehicle") {
        for (const m of sq.models) {
          if (!m.alive) continue;
          liveV.add(m.id);
          let e = this.vehicles.get(m.id);
          if (!e) {
            e = this.buildVehicle(sq);
            this.vehicles.set(m.id, e);
          }
          const p = this.interp(m.prevPos, m.pos, view.alpha);
          const f = this.facingInterp(m.prevFacing, m.facing, view.alpha);
          e.setPosition(p.x, 0, p.y);
          e.setLocalEulerAngles(0, -f * R2D, 0);
        }
      } else if (sq.def.role === "hero") {
        for (const m of sq.models) {
          if (!m.alive) continue;
          liveH.add(m.id);
          let h = this.heroes.get(m.id);
          if (!h) {
            h = this.buildHero(sq);
            this.heroes.set(m.id, h);
          }
          const p = this.interp(m.prevPos, m.pos, view.alpha);
          const f = this.facingInterp(m.prevFacing, m.facing, view.alpha);
          h.root.setPosition(p.x, 0, p.y);
          h.root.setLocalEulerAngles(0, -f * R2D, 0);
          const pulse = 1 + Math.sin(view.animTime * 4) * 0.08;
          h.aura.setLocalScale(150 * pulse, 2, 150 * pulse);
          h.aura.setPosition(p.x, 1.5, p.y);
        }
      }
    }
    for (const [id, e] of this.vehicles) if (!liveV.has(id)) { e.destroy(); this.vehicles.delete(id); }
    for (const [id, h] of this.heroes) if (!liveH.has(id)) { h.root.destroy(); h.aura.destroy(); this.heroes.delete(id); }
  }

  private buildVehicle(sq: Squad): pc.Entity {
    const root = new pc.Entity("vehicle");
    this.app.root.addChild(root);
    const body = this.matForFaction(sq.faction, "body");
    const trim = this.matForFaction(sq.faction, "trim");
    const steel = this.matForFaction(sq.faction, "steel");
    const add = (m: pc.StandardMaterial, x: number, y: number, z: number, sx: number, sy: number, sz: number, mesh = this.boxMesh, ry = 0, rz = 0) => {
      const e = new pc.Entity();
      e.addComponent("render", { meshInstances: [new pc.MeshInstance(mesh, m)] });
      e.setLocalScale(sx, sy, sz);
      e.setLocalEulerAngles(0, ry, rz);
      e.setLocalPosition(x, y, z);
      root.addChild(e);
    };
    if (sq.def.silhouette === "tank") {
      add(this.M.dark, 0, 8, -22, 90, 16, 20); // tread L
      add(this.M.dark, 0, 8, 22, 90, 16, 20); // tread R
      add(body, 0, 20, 0, 76, 18, 36);
      add(trim, -32, 20, 0, 12, 18, 36);
      add(steel, 0, 34, 0, 34, 16, 34, this.cylMesh);
      add(this.M.dark, 34, 34, 0, 56, 7, 7); // barrel
    } else {
      add(this.M.dark, 0, 8, 0, 70, 12, 28);
      add(trim, 6, 18, 0, 30, 14, 22);
      for (const wz of [-16, 16]) {
        add(this.M.dark, -22, 8, wz, 20, 20, 8, this.cylMesh, 0, 90);
        add(this.M.dark, 22, 8, wz, 20, 20, 8, this.cylMesh, 0, 90);
      }
      add(steel, 40, 10, 0, 26, 4, 4); // spike
    }
    return root;
  }

  private buildHero(sq: Squad): { root: pc.Entity; aura: pc.Entity } {
    const f = sq.faction;
    const root = new pc.Entity("hero");
    this.app.root.addChild(root);
    const body = this.matForFaction(f, "body");
    const trim = this.matForFaction(f, "trim");
    const steel = this.matForFaction(f, "steel");
    const add = (m: pc.StandardMaterial, x: number, y: number, z: number, sx: number, sy: number, sz: number, mesh = this.boxMesh, rz = 0) => {
      const e = new pc.Entity();
      e.addComponent("render", { meshInstances: [new pc.MeshInstance(mesh, m)] });
      e.setLocalScale(sx, sy, sz);
      e.setLocalEulerAngles(0, 0, rz);
      e.setLocalPosition(x, y, z);
      root.addChild(e);
    };
    add(this.M.dark, -3, 8, -4, 5, 16, 5);
    add(this.M.dark, -3, 8, 4, 5, 16, 5);
    add(body, 0, 22, 0, 12, 18, 16);
    add(trim, -9, 18, 0, 4, 26, 16); // cape
    add(steel, 3, 34, 0, 10, 10, 10); // head
    add(f === "legion" ? this.M.heroWeaponLegion : this.M.heroWeaponKindled, 14, 30, 6, 30, 4, 4, this.boxMesh, 40); // glowing weapon
    const aura = new pc.Entity("aura");
    aura.addComponent("render", { meshInstances: [new pc.MeshInstance(this.cylMesh, f === "legion" ? this.M.heroAuraLegion : this.M.heroAuraKindled)] });
    aura.setLocalScale(150, 2, 150);
    this.app.root.addChild(aura);
    return { root, aura };
  }

  // ---- effects: glow discs (pooled) ----
  private updateGlows(world: World): void {
    let gi = 0;
    for (const e of world.effects) {
      const k = e.t / e.ttl;
      let m: pc.StandardMaterial | null = null;
      let height = 4;
      let size = 8;
      switch (e.kind) {
        case "fire":
          m = this.glowMats.fire; height = 6; size = (e.size ?? 40) * (0.6 + k * 0.7); break;
        case "muzzle":
          m = this.glowMats.muzzle; height = 12; size = 10 * (1 - k) + 5; break;
        case "blood":
          m = e.faction === "kindled" ? this.glowMats.bloodKindled : this.glowMats.bloodLegion; height = 4; size = (e.size ?? 3) * (2 + k * 3); break;
        case "dust":
          m = this.glowMats.dust; height = 3; size = (e.size ?? 6) * (2 + k * 3); break;
        case "smoke":
          m = this.glowMats.smoke; height = 8; size = (e.size ?? 10) * (2 + k * 3); break;
        case "explosion":
          m = this.glowMats.explosion; height = 8; size = (e.size ?? 20) * (0.8 + k * 1.6); break;
        default:
          continue;
      }
      if (gi >= this.glowPool.length) break;
      const ent = this.glowPool[gi++];
      ent.enabled = true;
      ent.render!.meshInstances[0].material = m;
      const fade = 1 - k;
      ent.setLocalScale(size * fade * 2, 1, size * fade * 2);
      ent.setPosition(e.a.x, height, e.a.y);
    }
    for (; gi < this.glowPool.length; gi++) this.glowPool[gi].enabled = false;
  }

  // ---- effects: immediate-mode lines/rings, synced to engine render ----
  private drawImmediate(): void {
    const world = this.lastWorld;
    if (!world) return;
    const yTracer = 14;
    for (const e of world.effects) {
      const k = e.t / e.ttl;
      if (e.kind === "tracer" && e.b) {
        const col = parseColor(e.color, TRACER_FB);
        col.a = Math.max(0, 1 - k);
        this.app.drawLine(new pc.Vec3(e.a.x, yTracer, e.a.y), new pc.Vec3(e.b.x, yTracer, e.b.y), col);
      } else if (e.kind === "explosion" || e.kind === "flash") {
        const col = e.faction === "kindled" ? new pc.Color(1, 0.55, 0.2) : new pc.Color(1, 0.78, 0.5);
        col.a = Math.max(0, 1 - k);
        const r = (e.size ?? 20) * (0.4 + k);
        this.app.drawWireSphere(new pc.Vec3(e.a.x, 10, e.a.y), r, col, 20, true);
      }
    }
    // strategic-point capture-radius rings on the ground
    for (const p of world.points) {
      const col = p.contested ? RING_CONTESTED : p.owner === "legion" ? RING_LEGION : p.owner === "kindled" ? RING_KINDLED : RING_NEUTRAL;
      this.ring(p.pos.x, p.pos.y, p.captureRadius, col);
    }
  }

  private ring(cx: number, cy: number, r: number, col: pc.Color): void {
    const seg = 40;
    let px = cx + r;
    let pz = cy;
    for (let i = 1; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const nx = cx + Math.cos(a) * r;
      const nz = cy + Math.sin(a) * r;
      this.app.drawLine(new pc.Vec3(px, 1.5, pz), new pc.Vec3(nx, 1.5, nz), col);
      px = nx;
      pz = nz;
    }
  }

  resize(): void {
    this.app.resizeCanvas();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.camera.setViewport(window.innerWidth, window.innerHeight, dpr);
  }

  destroy(): void {
    this.soldiers.destroy();
    this.app.destroy();
    this.canvas.remove();
    void this.lastView;
    void D2R;
  }
}

const TRACER_FB = new pc.Color(1, 0.81, 0.44);
const RING_LEGION = new pc.Color(0.8, 0.2, 0.19, 0.5);
const RING_KINDLED = new pc.Color(0.88, 0.47, 0.11, 0.5);
const RING_NEUTRAL = new pc.Color(0.54, 0.52, 0.47, 0.4);
const RING_CONTESTED = new pc.Color(1, 0.82, 0.29, 0.7);
